// components/company/RequirementsStatusCard.jsx
import { useMemo } from "react";

function canonicalizeDocKind(input) {
  const raw = String(input ?? "").trim();
  if (!raw) return "";
  const upper = raw.toUpperCase().replace(/\s+/g, "_");
  if (upper === "PASSPORT") return "ID";
  if (upper === "ID_CARD") return "ID";
  if (upper === "IDENTITY_CARD") return "ID";
  return upper;
}

export function RequirementsStatusCard({ requirements = [], docs = [], waivers = [], onScrollToUpload, onWaiverRequest, onWaiverRemove }) {
  const status = useMemo(() => {
    try {
      if (!Array.isArray(requirements)) {
        console.error("[RequirementsStatusCard] Invalid requirements - not an array:", requirements);
        return {
          total: 0,
          complete: 0,
          missing: [],
          completeList: [],
          waivedList: [],
          percentage: 0,
          error: "Invalid requirements data",
        };
      }

      if (!Array.isArray(docs)) {
        console.error("[RequirementsStatusCard] Invalid docs - not an array:", docs);
        return {
          total: 0,
          complete: 0,
          missing: [],
          completeList: [],
          waivedList: [],
          percentage: 0,
          error: "Invalid documents data",
        };
      }

      if (!Array.isArray(waivers)) {
        console.error("[RequirementsStatusCard] Invalid waivers - not an array:", waivers);
      }

      const mandatory = requirements.filter((req) => {
        if (!req || typeof req !== "object") {
          console.warn("[RequirementsStatusCard] Skipping invalid requirement:", req);
          return false;
        }
        return req.mandatory;
      });

      const waiverMap = new Map();
      (Array.isArray(waivers) ? waivers : []).forEach((waiver) => {
        if (waiver && waiver.doc_kind) {
          const normalized = String(waiver.doc_kind || "").toUpperCase();
          waiverMap.set(normalized, waiver);
        }
      });

      const missing = [];
      const completeList = [];
      const waivedList = [];

      mandatory.forEach((req) => {
        try {
          if (!req || !req.doc_kind) {
            console.warn("[RequirementsStatusCard] Skipping requirement with no doc_kind:", req);
            return;
          }

          const reqKind = String(req.doc_kind || "").toUpperCase();
          const waiver = waiverMap.get(reqKind);

          if (waiver) {
            waivedList.push({
              kind: req.doc_kind,
              label: req.label || formatDocKind(req.doc_kind),
              waiver: waiver,
            });
            return;
          }

          const matchingDocs = docs.filter((doc) => {
            try {
              if (!doc || typeof doc !== "object") {
                console.warn("[RequirementsStatusCard] Skipping invalid doc:", doc);
                return false;
              }

              const docKind = canonicalizeDocKind(doc.kind);
              if (!docKind) return false;
              const reqKindCanon = canonicalizeDocKind(reqKind);
              if (docKind !== reqKindCanon) return false;
              if (doc.status === "rejected") return false;

              if (doc.expires_on) {
                try {
                  const expiry = new Date(doc.expires_on);
                  if (!isNaN(expiry.getTime()) && expiry < new Date()) {
                    return false;
                  }
                } catch (dateErr) {
                  console.warn("[RequirementsStatusCard] Invalid expiry date:", doc.expires_on, dateErr);
                }
              }

              return true;
            } catch (docErr) {
              console.error("[RequirementsStatusCard] Error processing doc:", doc, docErr);
              return false;
            }
          });

          if (matchingDocs.length > 0) {
            completeList.push({
              kind: req.doc_kind,
              label: req.label || formatDocKind(req.doc_kind),
              count: matchingDocs.length,
            });
          } else {
            missing.push({
              kind: req.doc_kind,
              label: req.label || formatDocKind(req.doc_kind),
            });
          }
        } catch (reqErr) {
          console.error("[RequirementsStatusCard] Error processing requirement:", req, reqErr);
        }
      });

      const totalComplete = completeList.length + waivedList.length;

      return {
        total: mandatory.length,
        complete: completeList.length,
        waived: waivedList.length,
        missing,
        completeList,
        waivedList,
        percentage: mandatory.length > 0 ? Math.round((totalComplete / mandatory.length) * 100) : 0,
        error: null,
      };
    } catch (err) {
      console.error("[RequirementsStatusCard] CRITICAL ERROR in status computation:", err);
      console.error("[RequirementsStatusCard] Requirements:", requirements);
      console.error("[RequirementsStatusCard] Docs:", docs);
      console.error("[RequirementsStatusCard] Waivers:", waivers);

      return {
        total: 0,
        complete: 0,
        waived: 0,
        missing: [],
        completeList: [],
        waivedList: [],
        percentage: 0,
        error: err.message || "Unknown error computing status",
      };
    }
  }, [requirements, docs, waivers]);

  const formatDocKind = (kind) => {
    try {
      if (!kind) return "Unknown";
      return String(kind)
        .replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    } catch (err) {
      console.error("[RequirementsStatusCard] Error formatting doc kind:", kind, err);
      return String(kind || "Unknown");
    }
  };

  const handleRemoveWaiver = async (waiverId) => {
    if (!onWaiverRemove) {
      console.warn("[RequirementsStatusCard] onWaiverRemove not provided");
      return;
    }

    if (!confirm("Remove this waiver? The document will be required again.")) {
      return;
    }

    try {
      await onWaiverRemove(waiverId);
    } catch (err) {
      console.error("[RequirementsStatusCard] Error removing waiver:", err);
      alert("Failed to remove waiver. Please try again.");
    }
  };

  if (status.error) {
    return (
      <div
        className="card"
        style={{
          marginBottom: "24px",
          backgroundColor: "#fef2f2",
          border: "1px solid #fca5a5",
          borderRadius: "8px",
          padding: "20px",
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "#ef4444",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              fontWeight: "bold",
            }}>
            !
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "#991b1b" }}>Error Loading Requirements Status</h3>
            <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#7c2d12" }}>{status.error}</p>
            <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#92400e" }}>Please check the browser console for details or contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status.total === 0) return null;

  const isComplete = status.percentage === 100;
  const isPartial = status.percentage > 0 && status.percentage < 100;
  const isEmpty = status.percentage === 0;

  return (
    <div
      className="card"
      style={{
        marginBottom: "24px",
        backgroundColor: isComplete ? "#f0fdf4" : isEmpty ? "#fef2f2" : "#fffbeb",
        border: `1px solid ${isComplete ? "#86efac" : isEmpty ? "#fca5a5" : "#fde68a"}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${isComplete ? "#bbf7d0" : isEmpty ? "#fecaca" : "#fef08a"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: isComplete ? "#22c55e" : isEmpty ? "#ef4444" : "#f59e0b",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              fontWeight: "bold",
            }}>
            {isComplete ? "✓" : isEmpty ? "!" : "◐"}
          </div>

          <div>
            <h3
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "600",
                color: isComplete ? "#166534" : isEmpty ? "#991b1b" : "#92400e",
              }}>
              Document Requirements
            </h3>
            <p
              style={{
                margin: "2px 0 0 0",
                fontSize: "14px",
                color: isComplete ? "#15803d" : isEmpty ? "#b91c1c" : "#b45309",
              }}>
              {status.complete + status.waived} of {status.total} complete ({status.percentage}%)
              {status.waived > 0 && <span style={{ marginLeft: "8px", fontSize: "12px" }}>({status.waived} waived)</span>}
            </p>
          </div>
        </div>

        <div
          style={{
            width: "200px",
            height: "8px",
            backgroundColor: "#e5e7eb",
            borderRadius: "4px",
            overflow: "hidden",
          }}>
          <div
            style={{
              width: `${status.percentage}%`,
              height: "100%",
              backgroundColor: isComplete ? "#22c55e" : isEmpty ? "#ef4444" : "#f59e0b",
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      <div style={{ padding: "20px" }}>
        {status.missing.length > 0 && (
          <div style={{ marginBottom: status.completeList.length > 0 || status.waivedList.length > 0 ? "20px" : "0" }}>
            <h4
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                fontWeight: "600",
                color: "#991b1b",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <span
                style={{
                  display: "inline-block",
                  width: "20px",
                  height: "20px",
                  borderRadius: "4px",
                  backgroundColor: "#fca5a5",
                  color: "#7f1d1d",
                  fontSize: "12px",
                  fontWeight: "bold",
                  textAlign: "center",
                  lineHeight: "20px",
                }}>
                {status.missing.length}
              </span>
              Missing
            </h4>
            <ul
              style={{
                margin: 0,
                padding: "0 0 0 28px",
                listStyle: "none",
              }}>
              {status.missing.map((doc, idx) => (
                <li
                  key={`missing-${doc.kind}-${idx}`}
                  style={{
                    marginBottom: "12px",
                    color: "#7c2d12",
                    fontSize: "14px",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                  <span style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        position: "absolute",
                        left: "-20px",
                        color: "#ef4444",
                        fontWeight: "bold",
                      }}>
                      ✕
                    </span>
                    {doc.label}
                  </span>
                  {onWaiverRequest && (
                    <button
                      type="button"
                      onClick={() => onWaiverRequest(doc)}
                      style={{
                        padding: "4px 12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        borderRadius: "4px",
                        border: "1px solid #9ca3af",
                        backgroundColor: "white",
                        color: "#6b7280",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#f3f4f6";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "white";
                      }}>
                      Mark as N/A
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {status.waivedList.length > 0 && (
          <div style={{ marginBottom: status.completeList.length > 0 ? "20px" : "0" }}>
            <h4
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                fontWeight: "600",
                color: "#2563eb",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <span
                style={{
                  display: "inline-block",
                  width: "20px",
                  height: "20px",
                  borderRadius: "4px",
                  backgroundColor: "#93c5fd",
                  color: "#1e3a8a",
                  fontSize: "12px",
                  fontWeight: "bold",
                  textAlign: "center",
                  lineHeight: "20px",
                }}>
                {status.waivedList.length}
              </span>
              Waived (Not Applicable)
            </h4>
            <ul
              style={{
                margin: 0,
                padding: "0 0 0 28px",
                listStyle: "none",
              }}>
              {status.waivedList.map((doc, idx) => (
                <li
                  key={`waived-${doc.kind}-${idx}`}
                  style={{
                    marginBottom: "12px",
                    fontSize: "14px",
                    position: "relative",
                  }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                    <span
                      style={{
                        position: "absolute",
                        left: "-20px",
                        color: "#2563eb",
                        fontWeight: "bold",
                      }}>
                      ℹ
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "#1e40af", fontWeight: "500" }}>{doc.label}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>Reason: {doc.waiver.reason}</div>
                    </div>
                    {onWaiverRemove && (
                      <button
                        type="button"
                        onClick={() => handleRemoveWaiver(doc.waiver.id)}
                        style={{
                          padding: "4px 12px",
                          fontSize: "12px",
                          fontWeight: "500",
                          borderRadius: "4px",
                          border: "1px solid #dc2626",
                          backgroundColor: "white",
                          color: "#dc2626",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#fef2f2";
                          e.target.style.borderColor = "#b91c1c";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "white";
                          e.target.style.borderColor = "#dc2626";
                        }}>
                        Remove
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {status.completeList.length > 0 && (
          <div>
            <h4
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                fontWeight: "600",
                color: "#166534",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <span
                style={{
                  display: "inline-block",
                  width: "20px",
                  height: "20px",
                  borderRadius: "4px",
                  backgroundColor: "#86efac",
                  color: "#14532d",
                  fontSize: "12px",
                  fontWeight: "bold",
                  textAlign: "center",
                  lineHeight: "20px",
                }}>
                {status.completeList.length}
              </span>
              Complete
            </h4>
            <ul
              style={{
                margin: 0,
                padding: "0 0 0 28px",
                listStyle: "none",
              }}>
              {status.completeList.map((doc, idx) => (
                <li
                  key={`complete-${doc.kind}-${idx}`}
                  style={{
                    marginBottom: "8px",
                    color: "#15803d",
                    fontSize: "14px",
                    position: "relative",
                  }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "-20px",
                      color: "#22c55e",
                      fontWeight: "bold",
                    }}>
                    ✓
                  </span>
                  {doc.label}
                  {doc.count > 1 && (
                    <span
                      style={{
                        marginLeft: "6px",
                        fontSize: "12px",
                        color: "#16a34a",
                        fontWeight: "500",
                      }}>
                      ({doc.count})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {status.missing.length > 0 && onScrollToUpload && (
          <div style={{ marginTop: "16px" }}>
            <button
              type="button"
              onClick={onScrollToUpload}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "500",
                borderRadius: "6px",
                border: "none",
                backgroundColor: "#2563eb",
                color: "white",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#1d4ed8")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#2563eb")}>
              Upload Missing Documents
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
